import { useMemo, useCallback } from 'react'
import { uniqueBy } from 'lib/array/arrayUtils'

interface OptionTypeData {
  typeId: string
  typeName: string
  position: number
  isColor: boolean
  options: Array<App.MerchandiseOfferOption | App.MerchandiseOfferColourOption>
  selectedOption: App.MerchandiseOfferOption | App.MerchandiseOfferColourOption | undefined
}

interface UseMerchandiseOptionDataResult {
  optionTypeData: Array<OptionTypeData>
  onOptionSelect: (position: number, optionValueName: string) => App.MerchandiseOfferVariant | undefined
}

function useMerchandiseOptionData(
  offer: App.MerchandiseOffer,
  variant: App.MerchandiseOfferVariant,
): UseMerchandiseOptionDataResult {
  // Collect option type positions from first variant (e.g., Size at position 0, Size at position 1)
  const orderedOptionTypeIds = useMemo(() => {
    if (offer.variants.length === 0) {
      return []
    }

    const templateVariant = offer.variants[0]

    return templateVariant.options?.map((option, index) => ({
      typeId: `${option.type}_${index}`,
      typeName: option.type,
      displayType: option.displayType,
      position: index,
    })) ?? []
  }, [offer.variants])

  const optionTypeData = useMemo(() => {
    return orderedOptionTypeIds.map(({ typeId, typeName, displayType, position }) => {
      const isColor = displayType === 'colour'

      const allOptionsAtPosition: Array<App.MerchandiseOfferOption | App.MerchandiseOfferColourOption> = []

      for (const v of offer.variants) {
        const optionAtPosition = v.options?.[position]
        if (optionAtPosition?.type === typeName) {
          allOptionsAtPosition.push(optionAtPosition)
        }
      }

      const uniqueOptions = uniqueBy(allOptionsAtPosition, opt => opt.name)
      const sortedOptions = uniqueOptions.sort((a, b) => a.position - b.position)
      const selectedOption = variant.options?.[position]

      return {
        typeId,
        typeName,
        position,
        isColor,
        options: sortedOptions,
        selectedOption,
      }
    })
  }, [orderedOptionTypeIds, offer.variants, variant.options])

  const onOptionSelect = useCallback((position: number, optionValueName: string): App.MerchandiseOfferVariant | undefined => {
    const desiredOptionNames: Array<string> = variant.options?.map((opt, i) =>
      i === position ? optionValueName : opt.name,
    ) ?? []

    // Find variant matching clicked option that best preserves other selections
    const candidateVariants = offer.variants
      .filter(v => v.options?.[position]?.name === optionValueName)
      .map(v => ({
        variant: v,
        matchScore: v.options?.filter((opt, i) => opt.name === desiredOptionNames[i]).length ?? 0,
      }))
      .sort((a, b) => b.matchScore - a.matchScore)

    return candidateVariants[0]?.variant
  }, [offer.variants, variant.options])

  return {
    optionTypeData,
    onOptionSelect,
  }
}

export default useMerchandiseOptionData
